import { WeaveAssistantLocationSetting_Hours } from '@weave/schema-gen-ts/dist/schemas/messaging/settings/v1/settings.pb';
import { PickRequired } from '@frontend/types';
import { SchemaMessagingSettingsV1Service } from './service';
import { MergedSettings, QueryEndpointName } from './types';

export const STANDARD_QUERY_ENDPOINT_NAMES = [
  'Get',
  'List',
  'Types',
  'History',
  'Types',
] as const satisfies (keyof typeof SchemaMessagingSettingsV1Service)[];
export const INFINITE_QUERY_ENDPOINT_NAMES = [] as const satisfies (keyof Omit<
  typeof SchemaMessagingSettingsV1Service,
  (typeof STANDARD_QUERY_ENDPOINT_NAMES)[number]
>)[];
export const QUERY_ENDPOINT_NAMES = [...STANDARD_QUERY_ENDPOINT_NAMES, ...INFINITE_QUERY_ENDPOINT_NAMES] as const;

export const MUTATION_ENDPOINT_NAMES = ['Set', 'BatchSet', 'Revert'] as const satisfies (keyof Omit<
  typeof SchemaMessagingSettingsV1Service,
  QueryEndpointName
>)[];

export const DEFAULT_SETTINGS: PickRequired<
  MergedSettings,
  | 'testSetting'
  | 'testNoDefaultSetting'
  | 'autoMessagingLocationSetting'
  | 'inboxLocationSetting'
  | 'inboxLocationUserSetting'
  | 'inboxUserSetting'
  | 'reputationNotificationsLocationUserSetting'
  | 'reputationUserSetting'
  | 'weaveAssistantLocationSetting'
  | 'aiReceptionistLocationUserSettings'
  | 'brandSetting'
> = {
  testSetting: {
    enabled: false,
    values: [],
  },
  testNoDefaultSetting: {
    enabled: false,
    values: [],
  },
  autoMessagingLocationSetting: {
    replyToEmail: '',
  },
  inboxLocationSetting: {
    enableActionableTab: false,
    processAutoMessagesSilently: false,
    processBulkMessagesSilently: false,
    processConfirmationsQuietly: false,
    processConfirmationsSilently: false,
    showUnrepliedStatus: false,
    moveBulkTextMessagesToTopOfInboxAfterSend: false,
  },
  inboxLocationUserSetting: {
    enableMessageNotifications: false,
    enableMessageNotificationsForConfirmations: false,
  },
  inboxUserSetting: {
    notificationSound: '',
  },
  reputationNotificationsLocationUserSetting: {
    facebookReviewSettings: {
      enableFacebookReviewNotifications: true,
      notifyNotRecommended: false,
      notifyRecommended: false,
    },
    googleReviewSettings: {
      enableGoogleReviewHigherNotifications: false,
      enableGoogleReviewLowerNotifications: true,
      enableGoogleReviewNotifications: true,
      googleReviewHigherThreshold: 4,
      googleReviewLowerThreshold: 3,
    },
    privateReviewSettings: {
      enablePrivateReviewNotifications: false,
    },
    textSurveySettings: {
      enableTextSurveyHigherNotifications: false,
      enableTextSurveyLowerNotifications: false,
      enableTextSurveyNotifications: true,
      textSurveyHigherThreshold: 7,
      textSurveyLowerThreshold: 3,
    },
  },
  reputationUserSetting: {
    notificationSound: '',
  },
  weaveAssistantLocationSetting: {
    enableWeaveAssistant: false,
    phoneSettings: {
      enableWeaveAssistantPhone: false,
      assistantFunctions: [],
    },
    smsSettings: {
      enableWeaveAssistantSms: false,
      assistantFunctions: [],
      contactTypes: [],
      hours: WeaveAssistantLocationSetting_Hours.ALL,
      hourTypes: [],
    },
    generalSettings: {
      businessAddress: '',
      businessWebsite: '',
      faqsLink: '',
      newPatientFormLinkEnglish: '',
      newPatientFormLinkSpanish: '',
      cancellationScript: '',
      emergencyScript: '',
    },
    paymentSettings: {
      paymentMethods: [],
      paymentDetails: '',
      insuranceDetails: '',
    },
    officeSettings: {
      officeAmenities: [],
      amenityDetails: '',
    },
    advancedSettings: {
      trainingNotes: [],
      trainingDocumentBuckets: [],
    },
  },
  aiReceptionistLocationUserSettings: {
    enableNeedsAttentionNotifications: false,
    enableSessionCompleteNotifications: false,
  },
  brandSetting: {
    customLinks: [],
    locationAddress: {
      address: '',
      address2: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
    },
    locationId: '',
    locationNickname: '',
    selectedPhoneNumberId: '',
  },
};
